<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import axios from 'axios';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

// --- SMS API Configuration ---
const SMS_API_BASE = 'https://smsgateway.garage.mn/api';
// IMPORTANT: Replace with your actual token.
// It is highly recommended to use environment variables for security.
const API_TOKEN = 'YOUR_GARAGE_MN_API_TOKEN';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Thumbnail,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showMergeModal: false,
      showDeleteModal: false,
      showSmsModal: false,
      smsMessage: '',
      isSendingSms: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'contacts/getUIFlags' }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
    // ++ NEW COMPUTED PROPERTY ++
    phoneOperator() {
      if (!this.contact.phone_number) {
        return null;
      }
      return this.getOperatorFromPhone(this.contact.phone_number);
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        if (id) {
          this.$store.dispatch('contacts/fetchContactableInbox', id);
        }
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openComposeConversationModal(toggleFn) {
      toggleFn();
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
    },
    closeComposeConversationModal() {
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({ name: 'inbox_view' });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({ name: 'contacts_dashboard' });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
    openSmsModal() {
      this.showSmsModal = true;
    },
    closeSmsModal() {
      this.showSmsModal = false;
      this.smsMessage = '';
    },
    async handleSendSms() {
      if (!this.smsMessage.trim()) {
        useAlert(
          this.$t('CONTACT_PANEL.SEND_SMS_MODAL.EMPTY_MESSAGE_ERROR'),
          'error'
        );
        return;
      }
      this.isSendingSms = true;
      try {
        const response = await axios.post(`${SMS_API_BASE}/send`, {
          phone: this.contact.phone_number,
          message: this.smsMessage,
          token: API_TOKEN,
        });
        if (response.data.status !== 'success') {
          throw new Error(response.data.message || 'SMS sending failed');
        }
        useAlert(
          this.$t('CONTACT_PANEL.SEND_SMS_MODAL.SUCCESS_MESSAGE'),
          'success'
        );
        this.closeSmsModal();
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message;
        useAlert(errorMessage, 'error');
      } finally {
        this.isSendingSms = false;
      }
    },
    // ++ NEW METHOD ++
    getOperatorFromPhone(phone) {
      const cleanPhone = phone.replace(/\D/g, '');

      if (cleanPhone.startsWith('976')) {
        const prefix = cleanPhone.substring(3, 5);
        switch (prefix) {
          case '99':
          case '95':
          case '94':
          case '93':
          case '92':
          case '91':
          case '90':
            return 'Mobicom';
          case '88':
          case '89':
          case '85':
          case '84':
          case '83':
          case '82':
          case '80':
            return 'Unitel';
          case '77':
          case '75':
          case '74':
          case '73':
          case '72':
          case '71':
          case '70':
            return 'Skytel';
          case '68':
          case '67':
          case '66':
          case '65':
          case '64':
          case '63':
          case '60':
            return 'G-Mobile';
          default:
            return 'Unknown';
        }
      }
      return 'Unknown';
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <Thumbnail
          v-if="showAvatar"
          :src="contact.thumbnail"
          size="48px"
          :username="contact.name"
          :status="contact.availability_status"
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <h3
            class="flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12"
          >
            {{ contact.name }}
          </h3>
          <div class="flex flex-row items-center gap-2">
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            show-copy
          />
          <ContactInfoRow
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
          />
          <!-- ++ NEW OPERATOR INFO ROW ++ -->
          <!-- Note: You may need to add 'CONTACT_PANEL.OPERATOR' to your language files -->
          <ContactInfoRow
            v-if="phoneOperator && phoneOperator !== 'Unknown'"
            :value="phoneOperator"
            icon="signal-high"
            emoji="📶"
            :title="$t('CONTACT_PANEL.OPERATOR')"
          />
          <!-- -- END OF NEW OPERATOR INFO ROW -- -->
          <ContactInfoRow
            :href="contact.plate_number ? `tel:${contact.plate_number}` : ''"
            :value="contact.plate_number"
            icon="map"
            emoji="🚗"
            :title="$t('CONTACT_PANEL.PLATE_NUMBER')"
            show-copy
          />
          <ContactInfoRow
            v-if="contact.identifier"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <ContactInfoRow
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
            :title="$t('CONTACT_PANEL.COMPANY')"
          />
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div class="flex items-center w-full mt-0.5 gap-2">
        <ComposeConversation
          :contact-id="String(contact.id)"
          is-modal
          @close="closeComposeConversationModal"
        >
          <template #trigger="{ toggle }">
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
              @click="openComposeConversationModal(toggle)"
            />
          </template>
        </ComposeConversation>
        <NextButton
          v-if="contact.phone_number"
          v-tooltip.top-end="$t('CONTACT_PANEL.SEND_SMS')"
          icon="i-ph-device-mobile"
          slate
          faded
          sm
          @click="openSmsModal"
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
          icon="i-ph-arrows-merge"
          slate
          faded
          sm
          :disabled="uiFlags.isMerging"
          @click="openMergeModal"
        />
        <NextButton
          v-if="isAdmin"
          v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="i-ph-trash"
          slate
          faded
          sm
          ruby
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
      </div>
      <EditContact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <ContactMergeModal
        v-if="showMergeModal"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="closeMergeModal"
      />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
    <div
      v-if="showSmsModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="closeSmsModal"
    >
      <div
        class="relative flex flex-col w-full max-w-lg mx-4 bg-white dark:bg-n-slate-800 rounded-lg shadow-xl"
      >
        <div
          class="flex items-center justify-between p-4 border-b border-solid border-n-slate-50 dark:border-n-slate-700"
        >
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ $t('CONTACT_PANEL.SEND_SMS_MODAL.TITLE') }}
          </h3>
          <NextButton
            icon="i-ph-x"
            variant="clear"
            color="secondary"
            @click="closeSmsModal"
          />
        </div>
        <div class="p-4">
          <textarea
            v-model="smsMessage"
            rows="5"
            class="w-full p-2 text-sm leading-6 transition-colors duration-200 ease-in-out bg-white border border-solid rounded-md resize-none border-n-slate-50 dark:bg-n-slate-900 text-n-slate-12 dark:border-n-slate-600 focus:border-w-primary-500"
            :placeholder="$t('CONTACT_PANEL.SEND_SMS_MODAL.PLACEHOLDER')"
          />
        </div>
        <div
          class="flex justify-end p-4 gap-2 bg-n-slate-25 dark:bg-n-slate-900 border-t border-solid border-n-slate-50 dark:border-n-slate-700 rounded-b-lg"
        >
          <NextButton color="secondary" variant="clear" @click="closeSmsModal">
            {{ $t('CONTACT_PANEL.SEND_SMS_MODAL.CANCEL') }}
          </NextButton>
          <NextButton
            :disabled="!smsMessage.trim() || isSendingSms"
            :loading="isSendingSms"
            @click="handleSendSms"
          >
            {{ $t('CONTACT_PANEL.SEND_SMS_MODAL.SEND') }}
          </NextButton>
        </div>
      </div>
    </div>
  </div>
</template>
